import "@uppy/core/css/style.css";
import "@uppy/dashboard/css/style.css";
import "./style.css"

import React, {useEffect, useMemo, useRef, useState} from "react";
import {Dashboard} from "@uppy/react";
import XHRUpload from "@uppy/xhr-upload";
import Uppy from "@uppy/core";
import {FiCheck, FiChevronDown, FiChevronRight, FiInfo, FiTrash2, FiX} from "react-icons/fi";

import ErrorBoundary from "../../../components/ErrorBoundary";
import {
    UserUploadPageUsage, getUploadPageUsage, queueUploadBatch,
    UPLOAD_ACCEPTED_EXTENSIONS, validateUploadFile,
} from "../../../api";
import {useGroupContext} from "../../../contexts/group_context";
import {useJobContext} from "../../../contexts/job_context";
import {useNavigationContext} from "../../../contexts/NavContext/nav-context";
import {withApiErrorHandler, withApiErrorHandlerThrow} from "../../../error_handling";
import {JobRead} from "../../../schemas";
import Swal from "sweetalert2";
import {UPLOAD_INSTRUCTIONS} from "../../../feature_flags";


// =========================
// Types & Utils (shared)
// =========================

// Accepted extensions and MIME types come from api/uploads.ts (single source of truth).
// Uppy needs file extensions in ".ext" format for restrictions.
const allowedFileTypes: string[] = Object.keys(UPLOAD_ACCEPTED_EXTENSIONS).map(ext => `.${ext}`);
const _allowedExtensionSet = new Set(Object.keys(UPLOAD_ACCEPTED_EXTENSIONS));


// One place to define how we derive a duplicate key (shareable).
const makeFileKey = (f: File) => `${f.name}::${f.size}::${f.lastModified}`;

// Toggle helpers (shareable between manual exclusion and auto-duplicate handling).
type Togglable = { excluded: boolean };
const setExcluded = <T extends Togglable>(obj: T, next: boolean): T => ({...obj, excluded: next});

// =========================
/** Upload structures */
// =========================

interface UploadFile {
    id: string;
    file: File;
    key: string;
    excluded: boolean;
    isDuplicate: boolean;
    /** null = none/unknown, 'auto-duplicate' = auto set by dedupe, 'manual' = user toggle */
    excludeSource?: 'auto-duplicate' | 'manual' | null;
}


interface FolderGroup {
    id: string;
    name: string;
    files: UploadFile[];
    open: boolean;
    excluded: boolean;   // folder-level soft-delete
}

// =========================
// Duplicate detection & application (kept separate, reusable)
// =========================

/**
 * Duplicate detection overview
 * ------------------------------------------------------------
 * We define a file's "duplicate key" as:
 *   key = `${name}::${size}::${lastModified}`
 *
 * Scope:
 *   - Detection is global across all uploaded folders (i.e., two files in different folders
 *     with the same key are considered duplicates).
 *
 * Behavior:
 *   - We count occurrences of each key.
 *   - For keys with count === 1:
 *       * Mark as `isDuplicate=false`, preserve the file's `excluded` flag as-is.
 *   - For keys with count > 1 (duplicates exist):
 *       * The first time we encounter that key while walking folders/files in their current order:
 *           - Mark as `isDuplicate=true` but keep it INCLUDED (excluded=false).
 *       * Every subsequent occurrence of that same key:
 *           - Mark as `isDuplicate=true` AND auto-exclude it (excluded=true).
 *
 * Guarantees:
 *   - Exactly one instance per duplicate set remains included by default; the rest are excluded.
 *   - Manual include/exclude toggles can override the default; reapplying this function
 *     will restore the "one-included-others-excluded" rule based on the current ordering.
 *
 * Limitations:
 *   - Two different files with identical (name, size, lastModified) will be seen as duplicates
 *     even if their contents differ. This is intentional for a fast, client-side heuristic.
 *   - The "first occurrence" is based on the current in-memory order of folders/files;
 *     re-ordering (e.g., by renaming folder names then re-sorting in future) could change which
 *     instance is considered the first kept one when reapplying the flags.
 */
function applyDuplicateFlags(folders: FolderGroup[]): FolderGroup[] {
    const counts = new Map<string, number>();
    for (const folder of folders) {
        for (const uf of folder.files) {
            counts.set(uf.key, (counts.get(uf.key) ?? 0) + 1);
        }
    }

    const seenFirst = new Set<string>();

    return folders.map((folder) => ({
        ...folder,
        files: folder.files.map((uf) => {
            const c = counts.get(uf.key) ?? 0;

            if (c <= 1) {
                // unique -> clear dup badge but DO NOT touch manual exclude
                return {...uf, isDuplicate: false};
            }

            // mark duplicate badge regardless
            const next: UploadFile = {...uf, isDuplicate: true};

            // If user manually set include/exclude, do NOT override it.
            if (uf.excludeSource === 'manual') {
                return next;
            }

            // Auto behavior for duplicates (first kept, subsequent excluded)
            if (!seenFirst.has(uf.key)) {
                seenFirst.add(uf.key);
                // keep included, clear any auto flag
                next.excluded = false;
                next.excludeSource = null;
                return next;
            }

            // subsequent duplicates -> auto-exclude, but mark source as auto
            next.excluded = true;
            next.excludeSource = 'auto-duplicate';
            return next;
        }),
    }));
}


/**
 * If you want folder-level exclude to cascade to files (but keep duplicates logic intact),
 * use this helper.
 */
function cascadeFolderExclusion(folder: FolderGroup, exclude: boolean): FolderGroup {
    return {
        ...folder,
        excluded: exclude,
        files: folder.files.map((f) => setExcluded(f, exclude || f.excluded)),
    };
}

// A single page-usage progress bar (e.g. "Today 120 / 500 pages").
const PageUsageBar: React.FC<{ label: string; used: number; limit: number }> = ({label, used, limit}) => {
    const ratio = limit > 0 ? used / limit : 0;
    const color = ratio >= 1 ? "bg-red-500" : ratio > 0.8 ? "bg-yellow-500" : "bg-blue-500";
    return (
        <div>
            <div className="flex justify-between items-center mb-1">
                <span className="text-gray-600">{label}</span>
                <span className="font-medium">
                    {used.toLocaleString()} / {limit.toLocaleString()} pages
                </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                    className={`h-2 rounded-full ${color}`}
                    style={{width: `${Math.min(100, ratio * 100)}%`}}
                />
            </div>
        </div>
    );
};

// =========================
// Component
// =========================

const FolderUpload: React.FC<{ close: (folderId?: string) => void }> = ({close}) => {
    const {currentGroup} = useGroupContext()!;
    const {addJobs} = useJobContext()!;
    const {navigateTo, JobsBlocker} = useNavigationContext()!;

    const [folders, setFolders] = useState<FolderGroup[]>([]);
    const [investigationContext, setInvestigationContext] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [pageUsage, setPageUsage] = useState<UserUploadPageUsage | null>(null);
    const uploadInProgressRef = useRef(false);

    // Unlimited when neither cap is set (UNLIMITED tier / superuser).
    const isUnlimited = pageUsage != null
        && pageUsage.daily_limit === null && pageUsage.lifetime_limit === null;

    // Create Uppy ONCE
    const uppy = useMemo(() => {
        const instance = new Uppy({
            autoProceed: false,
            restrictions: {
                maxFileSize: 1024 * 1024 * 200,
                allowedFileTypes,
            },
            infoTimeout: 12000,
        });
        instance.use(XHRUpload, {
            endpoint: "/api/staging",
            fieldName: "file",
            formData: true,
            bundle: false,
        });
        return instance;
    }, []);
    const handleRestartUpload = () => {
        if (loading) return;          // don't nuke mid-upload
        uppy.cancelAll();             // cancel any in-flight
        setFolders([]);               // clear your local folder state
    };

    // Load initial context from currentGroup
    useEffect(() => {
        if (currentGroup?.investigation_context) {
            setInvestigationContext(currentGroup.investigation_context);
        }
    }, [currentGroup]);

    // Fetch user upload page usage on mount
    useEffect(() => {
        const fetchUsage = async () => {
            const usage = await withApiErrorHandler(
                () => getUploadPageUsage(),
                {titleQualifier: "Error fetching upload usage", navigateTo}
            );
            if (usage) {
                setPageUsage(usage);
            }
        };
        fetchUsage();
    }, [navigateTo]);

    // Re-enable Esc-to-close
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" || e.key === "Esc") {
                if (loading) return; // prevent closing mid-upload
                e.preventDefault();
                close();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [close, loading]);

    // ✅ keep resize suppression
    useEffect(() => {
        const handler = (e: ErrorEvent) => {
            if (
                e.message.includes("ResizeObserver loop completed") ||
                e.message.includes("ResizeObserver loop limit exceeded")
            ) {
                e.stopImmediatePropagation();
            }
        };
        window.addEventListener("error", handler);
        return () => window.removeEventListener("error", handler);
    }, []);

    // Track tab visibility changes - uploads continue in background with retry logic
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && uploadInProgressRef.current) {
                console.log('[upload] Tab hidden - uploads continue with retry logic');
            } else if (!document.hidden && uploadInProgressRef.current) {
                console.log('[upload] Tab visible - upload still in progress');
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // OS junk files that should be silently ignored (never shown to user).
    const OS_JUNK = new Set(['.ds_store', 'thumbs.db', 'desktop.ini', '.thumbs', '__macosx']);

    // Collect skipped files as an inline hint (not a popup).
    const [skippedFiles, setSkippedFiles] = useState<Array<{ name: string; reason: string }>>([]);
    const skippedBufferRef = useRef<Array<{ name: string; reason: string }>>([]);
    const skippedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const flushSkippedRef = useRef(() => {});
    flushSkippedRef.current = () => {
        const buf = skippedBufferRef.current;
        if (buf.length === 0) return;
        skippedBufferRef.current = [];
        setSkippedFiles(prev => [...prev, ...buf]);
    };
    const addSkippedRef = useRef((_name: string, _reason: string) => {});
    addSkippedRef.current = (name: string, reason: string) => {
        // Silently ignore OS junk
        const baseName = name.split('/').pop() || name;
        if (OS_JUNK.has(baseName.toLowerCase())) return;
        skippedBufferRef.current.push({ name: baseName, reason });
        if (skippedTimerRef.current) clearTimeout(skippedTimerRef.current);
        skippedTimerRef.current = setTimeout(() => flushSkippedRef.current(), 500);
    };

    // Add files into folders; detect folder from relative path; apply duplicates after merge.
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error (Uppy types can be loose for custom meta)
        const onFileAdded = async (file: UppyFile) => {
            const rel = (file.meta?.relativePath as string) || file.name;
            const parts = rel.split("/");
            const now = new Date();
            const folderName = parts.length > 1 ? parts[0] : `Upload ${now.toLocaleDateString(undefined, {month: 'short', day: 'numeric'})} ${now.toLocaleTimeString(undefined, {hour: 'numeric', minute: '2-digit', second: '2-digit'})}`;


            const f = file.data as File;

            // Validate file type, size, and detect PTX format
            const validation = await validateUploadFile(f);
            if (validation.error) {
                uppy.removeFile(file.id);
                addSkippedRef.current(f.name, validation.error);
                return;
            }
            if (validation.warning) {
                void Swal.fire("Large file", validation.warning, "warning");
                // continue — warning doesn't block
            }
            if (validation.detectedType) {
                console.log(`[upload] PTX detected as ${validation.detectedType}: ${f.name}`);
            }

            const uploadFile: UploadFile = {
                id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2)}`,
                file: f,
                key: makeFileKey(f),
                excluded: false,
                isDuplicate: false,
                excludeSource: null,
            };

            console.log('f name', f.name);
            setFolders((prev) => {
                const existing = prev.find((fg) => fg.name === folderName);
                let next: FolderGroup[];
                if (existing) {
                    next = prev.map((fg) =>
                        fg.name === folderName
                            ? {...fg, files: [...fg.files, uploadFile], open: true}
                            : fg
                    );
                } else {
                    next = [
                        ...prev,
                        {
                            id: folderName + "_" + Date.now(),
                            name: folderName,
                            files: [uploadFile],
                            open: true,
                            excluded: false,
                        },
                    ];
                }
                // Apply duplicate flags AFTER merge (kept logically separate)
                return applyDuplicateFlags(next);
            });
        };

        // Catch Uppy's own restriction rejections (wrong extension, duplicate name, too large)
        // These fire BEFORE onFileAdded, so our validateUploadFile never sees them.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const onRestrictionFailed = (file: any, error: Error) => {
            const name = file?.name || file?.meta?.name || "Unknown file";
            console.log('[upload] restriction-failed:', name, error.message);
            addSkippedRef.current(name, error.message || "Restriction failed");
        };

        // Uppy also emits a general 'info-visible' event for batch restriction messages
        // like "X files could not be added". Capture those too.
        const onInfoVisible = () => {
            const state = uppy.getState();
            const info = state.info;
            if (info && Array.isArray(info)) {
                for (const msg of info) {
                    if (msg.type === 'error' && msg.message) {
                        console.log('[upload] info-visible error:', msg.message);
                        // These are batch messages like "8 files could not be added"
                        // The individual restriction-failed events have the details
                    }
                }
            }
        };

        uppy.on("file-added", onFileAdded);
        uppy.on("restriction-failed", onRestrictionFailed);
        uppy.on("info-visible", onInfoVisible);
        return () => {
            uppy.off("file-added", onFileAdded);
            uppy.off("restriction-failed", onRestrictionFailed);
            uppy.off("info-visible", onInfoVisible);
        };
    }, [uppy]);

    // Re-apply duplicate flags if you want to normalize after various edits.
    const reapplyDuplicates = () => setFolders((prev) => applyDuplicateFlags(prev));

    // Folder rename (re-apply duplicates to maintain one-kept rule consistently)
    const handleRename = (id: string, newName: string) =>
        setFolders((prev) => applyDuplicateFlags(prev.map((f) => (f.id === id ? {...f, name: newName} : f))));

    const toggleFolderOpen = (id: string) =>
        setFolders((prev) => prev.map((f) => (f.id === id ? {...f, open: !f.open} : f)));

    // Toggle folder include/exclude (soft delete)
    const toggleFolderInclude = (id: string) =>
        setFolders((prev) =>
            applyDuplicateFlags(
                prev.map((f) => (f.id === id ? cascadeFolderExclusion(f, !f.excluded) : f))
            )
        );

    // Toggle file include/exclude (soft delete)
    const toggleFileInclude = (folderId: string, fileId: string) =>
        setFolders((prev) =>
            applyDuplicateFlags(
                prev.map((fg) =>
                    fg.id !== folderId
                        ? fg
                        : {
                            ...fg,
                            files: fg.files.map((uf) =>
                                uf.id === fileId
                                    ? {
                                        ...uf,
                                        excluded: !uf.excluded,
                                        excludeSource: 'manual', // <-- manual override wins
                                    }
                                    : uf
                            ),
                        }
                )
            )
        );


    // Submit: only non-excluded files
    const handleSubmit = async () => {
        if (!currentGroup) {
            await Swal.fire("No group selected", "Please select a group before uploading", "error");
            return;
        }

        // Re-apply duplicates once more before submit to be safe
        reapplyDuplicates();

        const totalIncludedFiles = folders.reduce(
            (sum, fg) => sum + (fg.excluded ? 0 : fg.files.filter((f) => !f.excluded).length),
            0
        );
        if (totalIncludedFiles === 0) {
            await Swal.fire(
                "No files to upload",
                "All files are currently excluded. Toggle some back on to proceed.",
                "info"
            );
            return;
        }

        // Upload page quota is enforced server-side (per-page, daily + lifetime).
        // An over-quota batch is rejected with a 429 whose message is surfaced by
        // withApiErrorHandlerThrow below — no byte-based client pre-check needed.

        setLoading(true);
        uploadInProgressRef.current = true;
        const hasZip = folders.some(fg =>
            fg.files.some(f => f.file.name.toLowerCase().endsWith(".zip"))
        );
        const zipInstructions = hasZip ? `\nPlease note that only supported file types (${Object.keys(UPLOAD_ACCEPTED_EXTENSIONS).join(', ')}) will be read from the ZIP archives.` : '';
        // Show non-blocking loading modal (don't await - it stays open during upload)
        void Swal.fire({
            title: "Uploading...",
            text: "Your files are being uploaded. Do not close this window." + zipInstructions,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            // Build batched folder upload payload
            const foldersToUpload = folders
                .filter((f) => !f.excluded)
                .map((folder) => ({
                    folderName: folder.name,
                    files: folder.files.filter((f) => !f.excluded).map((f) => f.file),
                }))
                .filter((folder) => folder.files.length > 0);

            if (foldersToUpload.length === 0) {
                Swal.close();
                await Swal.fire("No files to upload", "All files are excluded.", "info");
                return;
            }

            // Single batched API call for all folders
            const jobs: JobRead[] = await withApiErrorHandlerThrow(
                () =>
                    queueUploadBatch(
                        foldersToUpload,
                        currentGroup.id,
                        500,
                        investigationContext,
                    ),
                {titleQualifier: "Error Uploading Files", navigateTo}
            );
            addJobs(jobs);

            // Close loading modal before showing success
            Swal.close();

            const includedFolders = folders.filter(
                (fg) => !fg.excluded && fg.files.some((f) => !f.excluded)
            );
            const includedFilesCount = includedFolders.reduce(
                (sum, fg) => sum + fg.files.filter((f) => !f.excluded).length,
                0
            );
            await Swal.fire({
                icon: "success",
                title: "Upload received",
                text: `Processing ${includedFolders.length} folder${includedFolders.length !== 1 ? "s" : ""} with a total of ${includedFilesCount} file${includedFilesCount !== 1 ? "s" : ""} in the background.`,
                confirmButtonColor: "#2563eb",
            });

            close(); // close after success
        } catch (err) {
            console.error("Queue error", err);
            // Close loading modal before showing error
            Swal.close();
            await Swal.fire("Upload failed", "Something went wrong during upload.", "error");
        } finally {
            setLoading(false);
            uploadInProgressRef.current = false;
        }
    };

    return (
        <JobsBlocker onTagJobs>
            <div className="flex-grow max-w-8xl h-full mx-auto mt-2 bg-white rounded-lg shadow-lg overflow-auto">
                {/* Single-column layout: Guidance on top, main content below */}
                <div className="flex flex-col w-full h-full">
                    {/* 🔹 Folder upload guidance (moved to top) */}
                    {UPLOAD_INSTRUCTIONS && (
                        <aside className="w-full bg-blue-50/70 border-b p-4 md:p-6 flex flex-col gap-3 transition-all">
                            {/* Header - Fixed size */}
                            <div className="flex items-center gap-2 text-blue-900 shrink-0">
                                <FiInfo className="shrink-0 w-4 h-4 md:w-5 md:h-5"/>
                                <span className="font-bold text-xs md:text-sm uppercase tracking-wider">
                Folder upload guidance
            </span>
                            </div>

                            {/* Content - Fluid Text & Auto Height */}
                            <div className="
            text-blue-900/90 leading-relaxed
            /* Fluid font size: 12px on small, up to 14px on large */
            text-[clamp(0.75rem,1vw,0.875rem)]
            /* Let it grow, but cap it at 30% of the screen height before scrolling */
            max-h-[30vh] overflow-y-auto
            scrollbar-thin scrollbar-thumb-blue-200
        ">

                                <ul className="list-disc ml-5 space-y-2">
                                    <li>
                                        Upload folders to organize your files. Your folder structure is preserved in the
                                        workspace.
                                    </li>
                                    <li>
                                        <span className="font-medium text-blue-950">ZIP files</span> are automatically
                                        extracted, but only the supported file types.
                                        <span className="ml-1 text-green-700 font-medium">(Tip: ZIPs upload faster for large batches!)</span>
                                    </li>
                                    <li>
                                        Use <span className="font-semibold text-blue-950">X</span> to exclude and{" "}
                                        <span className="font-semibold text-blue-950">✓</span> to include files before
                                        uploading.
                                    </li>
                                </ul>
                            </div>
                        </aside>
                    )}

                    {/* 🔹 Main content below (Dashboard + Folder Explorer + Buttons) */}
                    <div className="flex-1 flex flex-col h-full">
                        {/* Top: Uppy Dashboard */}
                        <div className="flex-shrink-0 p-4 border-b">
                            {/* Manual "Note" section directly below for total control */}
                            <div className="mb-2 flex items-center gap-2 text-[13px] text-gray-500">
                                <span className="font-semibold uppercase tracking-wider text-gray-400">Supported:</span>
                                <div className="flex flex-wrap gap-1">
                                    {Object.keys(UPLOAD_ACCEPTED_EXTENSIONS).filter(ext => ext !== "ptx").map(ext => (
                                        <span key={ext}
                                              className="bg-gray-100 px-1.5 py-0.5 rounded font-mono border border-gray-200">
                    {ext}
                </span>
                                    ))}
                                </div>
                            </div>
                            <Dashboard
                                uppy={uppy}
                                proudlyDisplayPoweredByUppy={true}
                                showSelectedFiles={false}
                                height={300}
                                width="100%"
                                note="Drag & drop folders here, or click to browse"
                                hideUploadButton
                                hideCancelButton
                                hidePauseResumeButton
                                disableStatusBar
                                showProgressDetails={false}
                            />

                            {/* Inline skipped-files hint */}
                            {skippedFiles.length > 0 && (
                                <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium">
                                            {skippedFiles.length} file{skippedFiles.length !== 1 ? "s" : ""} skipped
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setSkippedFiles([])}
                                            className="text-amber-600 hover:text-amber-800 text-xs"
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                    <ul className="text-xs text-amber-700 space-y-0.5 max-h-24 overflow-y-auto">
                                        {skippedFiles.slice(0, 20).map((f, i) => (
                                            <li key={i}>{f.name} &mdash; {f.reason}</li>
                                        ))}
                                        {skippedFiles.length > 20 && (
                                            <li className="text-amber-500">...and {skippedFiles.length - 20} more</li>
                                        )}
                                    </ul>
                                </div>
                            )}

                        </div>
                        {/* 🔹 Investigation Context Input */}
                        <div className="p-4 bg-white border-b">
                            <h3 className="font-semibold text-base mb-2">🔎 Investigation Context</h3>
                            <p className="text-sm text-gray-600 mb-2">
                                Describe what the system should focus on while analyzing this corpus,
                                such as suspected issues, topics, time periods, or entities.
                            </p>

                            <textarea
                                className="
            w-full min-h-[120px] p-3 rounded-lg border border-slate-300
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            text-slate-700 placeholder-slate-400 resize-vertical bg-slate-50
        "
                                placeholder="Example: Investigate potential financial irregularities between 2018–2020 involving vendor contracts, emails referencing 'Project Falcon', and any deleted or suspicious ZIP archives."
                                value={investigationContext ?? ""}
                                onChange={(e) => setInvestigationContext(e.target.value)}
                            />

                            {/* Optional: Auto-fill from group */}
                            {currentGroup?.investigation_context && (
                                <button
                                    onClick={() =>
                                        setInvestigationContext(currentGroup.investigation_context ?? "")
                                    }
                                    className="mt-2 text-xs text-blue-600 hover:underline"
                                >
                                    Load saved group context
                                </button>
                            )}
                        </div>


                        {/* Folder Explorer */}
                        <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
                            <h3 className="font-semibold text-base mb-3">📂 Uploaded Folders</h3>

                            {/* Existing folder list content (unchanged) */}
                            <div className="space-y-3">
                                {folders.map((folder) => {
                                    const folderOpacity = folder.excluded ? "opacity-40" : "opacity-100";
                                    return (
                                        <div key={folder.id}
                                             className={`border rounded bg-white shadow-sm ${folderOpacity}`}>
                                            <div className="flex items-center gap-2 p-2">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleFolderOpen(folder.id)}
                                                    className="p-1 rounded hover:bg-gray-200"
                                                    aria-label={folder.open ? "Collapse folder" : "Expand folder"}
                                                >
                                                    {folder.open ? <FiChevronDown/> : <FiChevronRight/>}
                                                </button>

                                                {/* Folder name input */}
                                                <div className="flex-1">
                                                    <label className="block text-[11px] text-gray-500 mb-0.5">Folder
                                                        name</label>
                                                    <input
                                                        className="w-full border rounded px-2 py-1 text-sm font-medium text-blue-700 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                        value={folder.name}
                                                        onChange={(e) => handleRename(folder.id, e.target.value)}
                                                        aria-label="Folder name (editable)"
                                                        placeholder="Type to rename this folder…"
                                                    />
                                                </div>

                                                <span className="ml-1 text-xs text-gray-500 shrink-0">
                        {folder.files.length} file{folder.files.length !== 1 ? "s" : ""}
                      </span>

                                                {/* Include/Exclude toggle for folder */}
                                                <button
                                                    type="button"
                                                    onClick={() => toggleFolderInclude(folder.id)}
                                                    className={`ml-2 px-2 py-1 rounded text-sm border ${
                                                        folder.excluded
                                                            ? "border-green-600 text-green-700 hover:bg-green-50"
                                                            : "border-red-600 text-red-700 hover:bg-red-50"
                                                    }`}
                                                >
                                                    {folder.excluded ? (
                                                        <div className="flex items-center gap-1">
                                                            <FiCheck/> Include
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-1">
                                                            <FiX/> Exclude
                                                        </div>
                                                    )}
                                                </button>
                                            </div>

                                            {folder.open && (
                                                <ul className="mx-4 mb-3 space-y-1">
                                                    {folder.files.map((uf) => {
                                                        const transparent = uf.excluded ? "opacity-40" : "opacity-100";
                                                        return (
                                                            <li
                                                                key={uf.id}
                                                                className={`flex items-center justify-between px-2 py-1 text-sm rounded bg-gray-50 border ${transparent}`}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span>📄 {uf.file.name}</span>
                                                                    {uf.isDuplicate && (
                                                                        <span
                                                                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-300">
                                    Duplicate
                                  </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => toggleFileInclude(folder.id, uf.id)}
                                                                        className={`px-2 py-0.5 rounded text-xs border ${
                                                                            uf.excluded
                                                                                ? "border-green-600 text-green-700 hover:bg-green-50"
                                                                                : "border-red-600 text-red-700 hover:bg-red-50"
                                                                        }`}
                                                                    >
                                                                        {uf.excluded ? (
                                                                            <div className="flex items-center gap-1">
                                                                                <FiCheck/> Include
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex items-center gap-1">
                                                                                <FiX/> Exclude
                                                                            </div>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div className="p-4 border-t bg-white">
                            {/* Upload page-quota indicator (daily + lifetime) */}
                            {pageUsage && !isUnlimited && (
                                <div className="mb-3 p-2 rounded bg-gray-50 border text-sm space-y-2">
                                    {pageUsage.daily_limit !== null && (
                                        <PageUsageBar
                                            label="Today"
                                            used={pageUsage.pages_today}
                                            limit={pageUsage.daily_limit}
                                        />
                                    )}
                                    {pageUsage.lifetime_limit !== null && (
                                        <PageUsageBar
                                            label="Total"
                                            used={pageUsage.pages_lifetime}
                                            limit={pageUsage.lifetime_limit}
                                        />
                                    )}
                                </div>
                            )}
                            {isUnlimited && (
                                <div className="mb-3 p-2 rounded bg-green-50 border border-green-200 text-sm text-green-700">
                                    Unlimited uploads enabled
                                </div>
                            )}
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleRestartUpload}
                                    className="mr-auto px-3 py-2 border border-red-600 text-red-700 rounded-lg hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                                    disabled={loading}
                                >
                                    <FiTrash2/>
                                    Restart
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    disabled={loading}
                                >
                                    Submit All
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </JobsBlocker>
    );
};

// Wrap with ErrorBoundary to prevent white screen crashes
const FolderUploadWithErrorBoundary: React.FC<{ close: (folderId?: string) => void }> = (props) => (
    <ErrorBoundary
        onError={(error) => {
            console.error('[FolderUpload] Caught error:', error);
        }}
        onReset={() => {
            // Could trigger a refresh or re-init here if needed
        }}
    >
        <FolderUpload {...props} />
    </ErrorBoundary>
);

export default FolderUploadWithErrorBoundary;
